<script setup lang="ts">
import { inject, ref } from "vue";
import type { AccordionProps } from "../";

export interface AccordionDetailsProps {
  open?: boolean;
}

const { open = false } = defineProps<AccordionDetailsProps>();

const accordionProps = inject<AccordionProps>("accordionContext");
const detailsRef = ref<HTMLDetailsElement | null>(null);
const isOpen = ref(open);

defineExpose({ open: isOpen });

const onToggle = () => {
  isOpen.value = detailsRef.value?.open ?? false;
};
</script>

<template>
  <details
    ref="detailsRef"
    class="group focus-visible:outline-none"
    :class="{
      'border-t last-of-type:border-b': accordionProps?.divider,
      'pb-4': isOpen && accordionProps?.divider && accordionProps?.size === 'small',
      'pb-6': isOpen && accordionProps?.divider && accordionProps?.size === 'medium',
    }"
    :name="accordionProps?.name"
    :open="!!open"
    data-testid="clr-details"
    @toggle="onToggle"
  >
    <slot name="header" v-bind="$slots.default?.()[0]" />
    <slot />
  </details>
</template>

<style>
@supports (interpolate-size: allow-keywords) {
  details {
    interpolate-size: allow-keywords;
  }

  ::details-content {
    @apply h-0 overflow-clip;

    @media (prefers-reduced-motion: no-preference) {
      transition:
        all 300ms ease,
        content-visibility 300ms allow-discrete;
    }
  }

  [open]::details-content {
    @apply h-auto;
  }
}
</style>
